import { CheckCircle2, MessageSquare, Pencil, PlusCircle, UserPlus, type LucideIcon } from 'lucide-react';

import { formatDateTimeRO } from '../../../lib/format';
import {
    TICKET_PRIORITY_LABELS,
    TICKET_STATUS_LABELS,
    type TicketEvent,
    type TicketEventChange,
    type TicketEventType,
    type TicketPriority,
    type TicketStatus,
} from '../tickets.schema';

const ICONS: Record<TicketEventType, LucideIcon> = {
    created: PlusCircle,
    comment: MessageSquare,
    status_change: CheckCircle2,
    assignment: UserPlus,
    edit: Pencil,
};

const FIELD_LABELS: Record<string, string> = {
    title: 'Titlu',
    description: 'Detalii',
    priority: 'Prioritate',
    dueDate: 'Termen',
    labels: 'Etichete',
    references: 'Legaturi',
    attachments: 'Atasamente',
};

function statusLabel(s?: string | null): string {
    return s ? (TICKET_STATUS_LABELS[s as TicketStatus] ?? s) : '';
}

function truncate(s?: string | null, max = 60): string {
    if (!s) return '';
    return s.length > max ? `${s.slice(0, max)}…` : s;
}

// One change line for an 'edit' event, in plain Romanian.
function changeSummary(c: TicketEventChange): string {
    const name = FIELD_LABELS[c.field] ?? c.field;
    // List-shaped changes (labels, references, attachments): show +added / -removed.
    if (c.added || c.removed) {
        const parts = [
            ...(c.added ?? []).map((l) => `+${l}`),
            ...(c.removed ?? []).map((l) => `-${l}`),
        ];
        return `${name}: ${parts.join(', ')}`;
    }
    if (c.field === 'priority') {
        const f = c.from ? (TICKET_PRIORITY_LABELS[c.from as TicketPriority] ?? c.from) : '—';
        const t = c.to ? (TICKET_PRIORITY_LABELS[c.to as TicketPriority] ?? c.to) : '—';
        return `${name}: ${f} → ${t}`;
    }
    if (c.field === 'dueDate') {
        return `${name}: ${c.from ? formatDateTimeRO(c.from) : 'fara'} → ${c.to ? formatDateTimeRO(c.to) : 'fara'}`;
    }
    return `${name}: ${truncate(c.from) || '—'} → ${truncate(c.to) || '—'}`;
}

function describe(event: TicketEvent): string {
    switch (event.type) {
        case 'created':
            return 'a creat ticketul';
        case 'status_change':
            return `a schimbat statusul: ${statusLabel(event.fromStatus)} → ${statusLabel(event.toStatus)}`;
        case 'assignment':
            // body is the assignee snapshot, or the sentinel 'Neatribuit' when
            // the assignment was cleared — render that as a removal, not
            // "a atribuit lui Neatribuit".
            return event.body && event.body !== 'Neatribuit'
                ? `a atribuit lui ${event.body}`
                : 'a scos atribuirea';
        case 'comment':
            return 'a comentat';
        case 'edit':
            return 'a modificat ticketul';
        default:
            return '';
    }
}

export function TicketTimelineItem({ event }: { event: TicketEvent }) {
    const Icon = ICONS[event.type] ?? MessageSquare;
    const isComment = event.type === 'comment';
    const changes = event.type === 'edit' ? event.meta?.changes ?? [] : [];
    return (
        <li className="flex gap-3">
            <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300">
                <Icon aria-hidden="true" className="h-4 w-4" />
            </span>
            <div className="min-w-0 flex-1 pb-4">
                <p className="text-sm">
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                        {event.authorName || 'Sistem'}
                    </span>{' '}
                    <span className="text-gray-600 dark:text-gray-300">{describe(event)}</span>
                </p>
                {isComment && event.body && (
                    <p className="mt-1 whitespace-pre-wrap rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-800 dark:bg-gray-900/50 dark:text-gray-200">
                        {event.body}
                    </p>
                )}
                {changes.length > 0 && (
                    <ul className="mt-1 flex flex-col gap-0.5">
                        {changes.map((c, i) => (
                            <li
                                key={i}
                                className="break-words rounded-md bg-gray-50 px-3 py-1.5 text-xs text-gray-700 dark:bg-gray-900/50 dark:text-gray-300"
                            >
                                {changeSummary(c)}
                            </li>
                        ))}
                    </ul>
                )}
                <p className="mt-0.5 text-xs text-gray-400 dark:text-gray-500">
                    {formatDateTimeRO(event.createdAt)}
                </p>
            </div>
        </li>
    );
}
